import React from 'react';
import { FaFish, FaClipboardList, FaCalendarAlt, FaInfoCircle, FaEuroSign } from 'react-icons/fa';
import { PageHero, Section, CardLayout, Card, CtaButton } from '../components/PageLayout';
import { etangsImages, heroImages } from '../constants/imagePlaceholders';
import ClickableImage from '../components/ClickableImage';
import SEO from '../components/SEO';

interface TarifInfo {
  id: string;
  libelle: string;
  prix: string;
}

interface EtangInfo {
  id: number;
  name: string;
  description: string;
  tarifs: TarifInfo[];
  imageMain: string;
  imagesSupplementaires: string[];
}

const EtangsPage: React.FC = () => {
  const etangs: EtangInfo[] = [
    {
      id: 1,
      name: 'Réservoir de pêche à la mouche',
      description: 'Vous y retrouverez deux étangs spécialement aménagés pour la pratique de la pêche à la mouche. Avec de nombreuses truites résidentes dans ces derniers.<br /><br /><b>Réglementation :</b><br />- Pêche en <b>no-kill</b><br />- Hameçon sans ardillon<br />- Booby interdit<br />- 3 mouches maximum sur la ligne<br />- une seule canne en action de pêche.',
      tarifs: [
        { id: 'journee_mouche', libelle: 'Journée', prix: '20 €' },
      ],
      imageMain: etangsImages.grandEtang1,
      imagesSupplementaires: [
        etangsImages.grandEtangPortrait1,
        etangsImages.grandEtangPortrait2,
        etangsImages.grandEtang2
      ]
    },
    {
      id: 2,
      name: 'Étang des Vernes',
      description: 'Étang spécialement aménagée pour la pêche à la truite, empoissoné 4 fois par jour. <br />Parfait pour les familles et les débutants avec des eaux claires et peu profondes.<br /><br /><b>Réglementation :</b><br />- Pêche aux appâts naturels (teignes, vers de farine, vifs...)<br />- Amorçage et Asticôts interdits<br />- Leurres et mouches artificiels interdits<br />- Une ligne autorisée maximum par pêcheur<br />- Prise illimitée',
      tarifs: [
        { id: 'demi_journee_vernes', libelle: 'Demi-journée (8h - 13h ou 13h - 18h)', prix: '19 €' },
        { id: 'journee_vernes', libelle: 'Journée (8h - 18h)', prix: '36 €' },
      ],
      imageMain: etangsImages.etangTruites2,
      imagesSupplementaires: [
        etangsImages.etangTruitesPortrait1,
        etangsImages.etangTruites1,
        etangsImages.etangTruites3
      ]
    },
    {
      id: 3,
      name: 'Étang des Roches',
      description: 'Étang spécial grosses truites, empoissoné 2 fois par jour. <br />Idéal pour pêcher en famille ou entre amis dans un cadre reposant.<br /><br /><b>Réglementation :</b><br />- Pêche aux appâts naturels (teignes, vers de farine, vifs...)<br />- Amorçage et Asticôts interdits<br />- Leurres et mouches artificiels autorisés<br />- Cuillère et hameçon triple interdites<br />- 2 lignes maximum par pêcheur<br />- Prise illimitée',
      tarifs: [
        { id: 'demi_journee_vernes', libelle: 'Demi-journée (8h - 13h ou 13h - 18h)', prix: '28 €' },
        { id: 'journee_vernes', libelle: 'Journée (8h - 18h)', prix: '56 €' },
      ],
      imageMain: etangsImages.petitEtang1,
      imagesSupplementaires: [
        etangsImages.petitEtangPortrait1,
        etangsImages.ponton
      ]
    },
  ];

  return (
    <div className="font-body">
      <SEO 
        title="Étangs de Pêche"
        description="Découvrez nos 3 étangs de pêche aménagés (carpe, truite, pêche au coup) au Moulin Tala, dans le Morvan. Cadre naturel, pisciculture, règlement et tarifs."
        canonicalUrl="https://lemoulintala.fr/etangs"
        keywords="étang de pêche morvan, pêche carpe, pêche truite, pisciculture, pêche no-kill, brassy"
      />

      {/* Header Banner */}
      <PageHero 
        title="Nos Étangs de Pêche"
        subtitle="Étangs aménagés pour tous les types de pêche en eau douce"
        backgroundImage={heroImages.etangs}
      />

      {/* Introduction */}
      <Section 
        title="Pêchez dans un cadre naturel exceptionnel"
        subtitle="Bienvenue dans notre domaine de pêche du Moulin Tala. Situés au cœur du Morvan, nos étangs vous offrent des conditions de pêche privilégiées dans un environnement préservé."
        background="light"
        textAlign="center"
      >
        <div className="flex flex-col items-center">
          <p className="text-lg max-w-4xl mx-auto mb-8">
            Que vous soyez débutant ou confirmé, en famille ou entre amis, nous vous proposons des espaces adaptés à tous les types de pêche à la truite.
          </p>
          
          <div className="grid md:grid-cols-3 gap-4 w-full max-w-5xl">
            <ClickableImage
              src={etangsImages.riviere1}
              alt="Rivière traversant le domaine"
              title="Notre rivière"
              description="La rivière qui traverse le domaine du Moulin Tala"
              className="rounded-lg shadow-md"
              aspectRatio="3/4"
            />
            <ClickableImage
              src={etangsImages.pisciculturePortrait2}
              alt="Notre pisciculture"
              title="Pisciculture du Moulin Tala"
              description="Là où nous élevons nos truites pour les étangs"
              className="rounded-lg shadow-md"
              aspectRatio="3/4"
            />
            <ClickableImage
              src={etangsImages.riviere2}
              alt="Environnement naturel préservé"
              title="Cadre naturel préservé"
              description="La nature intacte qui entoure nos installations"
              className="rounded-lg shadow-md"
              aspectRatio="3/4"
            />
          </div>
        </div>
      </Section>

      {/* Étangs Section */}
      <Section title="Nos Étangs" paddingY="16" textAlign="center">
        <div className="space-y-16">
          {etangs.map((etang, index) => (
            <div 
              key={etang.id} 
              className={`grid md:grid-cols-2 gap-8 items-center ${
                index % 2 === 1 ? 'md:flex-row-reverse' : ''
              }`}
            >
              <div className={`${index % 2 === 1 ? 'md:order-2' : 'md:order-1'} space-y-4`}>
                <ClickableImage
                  src={etang.imageMain}
                  alt={etang.name}
                  title={etang.name}
                  description={etang.description.split('.')[0]}
                  className="shadow-xl rounded-lg overflow-hidden"
                  aspectRatio="16/9"
                />
                
                <div className="grid grid-cols-3 gap-2">
                  {etang.imagesSupplementaires.map((img, idx) => (
                    <ClickableImage 
                      key={idx}
                      src={img}
                      alt={`${etang.name} - vue ${idx + 1}`}
                      title={`${etang.name}`}
                      description={`Vue ${idx + 1} de notre ${etang.name.toLowerCase()}`}
                      className="rounded-lg shadow-md"
                      aspectRatio="4/3"
                    />
                  ))}
                </div>
              </div>
              
              <div className={`${index % 2 === 1 ? 'md:order-1' : 'md:order-2'} text-left`}>
                <h3 className="text-2xl font-bold mb-4 text-primary font-heading">{etang.name}</h3>
                <p className="text-lg mb-4" dangerouslySetInnerHTML={{ __html: etang.description }} />
                <div>
                  <h4 className="text-lg font-semibold mt-6 mb-3 font-heading text-gray-700">
                    Tarifs :
                  </h4>
                  <ul className="divide-y divide-gray-200">
                    {etang.tarifs.map((tarif) => (
                      <li key={tarif.id} className="flex justify-between py-3 items-center">
                        <span className="text-gray-600">{tarif.libelle}</span>
                        <span className="font-bold text-lg text-primary">{tarif.prix}</span>
                      </li>
                    ))}
                  </ul>
                  {etang.id === 1 && (
                    <p className="text-sm italic text-gray-600 mt-3">
                      Possibilité de ramener une truite à la maison, pêchée par nos soins à la pisciculture. Un avoir de 10€ sera fait sur le prix de celle-ci.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>
      
      {/* Pisciculture Section */}
      <Section background="light" title="Notre Pisciculture" paddingY="16" textAlign="center">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-lg mb-6">
                Il s'agit d'un ancien moulin, qui a été transformé en pisciculture où vous pouvez retrouver notre élevage de truites. Cella nous permet de garantir la qualité et la fraîcheur des poissons proposés à nos pêcheurs.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <ClickableImage
                src={etangsImages.pisciculture1}
                alt="Notre pisciculture - vue principale"
                title="Pisciculture du Moulin Tala"
                description="Vue générale de notre installation piscicole"
                className="shadow-md rounded-lg"
              />
              <ClickableImage
                src={etangsImages.pisciculturePortrait}
                alt="Bassins d'élevage"
                title="Bassins d'élevage"
                description="Là où grandissent nos truites"
                className="shadow-md rounded-lg"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* Rules Section */}
      <Section title="Vente au détail et pêche rapide" textAlign="center" paddingY="16">
        <div className="max-w-2xl mx-auto text-left">
          <Card shadow="lg" className="p-6 md:p-8">
            <p className="text-xl font-semibold mb-6 text-gray-800 text-center">
              Ventes de truites (10h30 - 17h45)
            </p>
            <div className="space-y-4">
              {/* Truite portion */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-3 border-b border-gray-200">
                <span className="text-md text-gray-700 mb-1 sm:mb-0">Truite portion (env. 300g)</span>
                <span className="font-bold text-lg text-primary sm:ml-4">13 €/kg</span>
              </div>
              {/* Grosse truite */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-3 border-b border-gray-200">
                <span className="text-md text-gray-700 mb-1 sm:mb-0">Grosse truite (à partir d'1kg)</span>
                <span className="font-bold text-lg text-primary sm:ml-4">15 €/kg</span>
              </div>
              {/* Planche de truite fumée */}
              <div className="pt-2 pb-3 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center ">
                  <span className="text-md text-gray-700 mb-1 sm:mb-0">Planche de truite fumée (400g, tranchée, désarêtée)</span>
                  <span className="font-bold text-lg text-primary sm:ml-4">20 €</span>
                </div>
                <p className="text-sm text-gray-500 text-right mt-1 sm:mt-0">
                  (soit 50 €/kg)
                </p>
              </div>
              {/* Pêche rapide */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-3 border-b border-gray-200">
                <span className="text-md text-gray-700 mb-1 sm:mb-0">Pêche rapide environ 15-20min (matériel et appât fourni)</span>
                <span className="font-bold text-lg text-primary sm:ml-4">13 €/kg</span>
              </div>
            </div>
          </Card>
        </div>
      </Section>

      {/* CTA */}
      <Section background="primary" textAlign="center" paddingY="12">
        <h2 className="text-3xl font-bold mb-6 text-white font-heading">Prêt à tenter votre chance ?</h2>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <CtaButton to="/tarifs" type="accent">
            Consulter les tarifs
          </CtaButton>
          <CtaButton to="/contact" type="outline">
            Nous contacter
          </CtaButton>
        </div>
      </Section>
    </div>
  );
};

export default EtangsPage; 